import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Leaf } from "lucide-react";
import { useCart } from "../context/CartContext";
import { useCatalog } from "../context/CatalogContext";
import { minPrice, getVariant, resolveImage } from "../data/products";
import ProductImage from "./ProductImage";
import AutumnLeaves, { CornerLeaves } from "./AutumnLeaves";
import { useSaleProductNames } from "./SalePrice";
import GhostMark from "./GhostMark";

const fmtDate = (iso) => new Date(`${iso}T12:00:00Z`).toLocaleDateString("en-US", { month: "short", day: "numeric" });
const daysLeft = (iso) => Math.max(0, Math.floor((new Date(`${iso}T23:59:59Z`) - Date.now()) / 86400000));

const PromoProduct = ({ product, pct, accent }) => {
  const v = getVariant(product, product.defaultVariant);
  const { src, fallback } = resolveImage(product, v);
  const from = minPrice(product);
  return (
    <Link
      to={`/product/${product.slug}`}
      data-testid={`home-seasonal-product-${product.slug}`}
      className="group relative flex-1 min-w-0 rounded-[20px] overflow-hidden bg-[#FDFAF9] border border-[#8B2252]/10 transition-transform duration-500 hover:-translate-y-1"
    >
      <div className="aspect-[4/5] overflow-hidden bg-cream-200">
        <ProductImage src={src} fallback={fallback} alt={product.name}
          className="w-full h-full object-cover [object-position:center_42%] transition-transform duration-700 group-hover:scale-[1.04]" />
      </div>
      <span style={{ background: accent }} className="absolute top-3 left-3 rounded-full text-white text-[10px] font-semibold tracking-[0.14em] uppercase px-2.5 py-1">
        {pct}% off
      </span>
      <div className="p-4">
        <p className="font-display font-bold text-lg text-[#1C1618] leading-none">{product.name}</p>
        <p className="mt-1.5 text-sm flex flex-wrap items-baseline gap-x-2">
          <span className="line-through text-[#9a7a80]">${from.toFixed(2)}</span>
          <span className="font-semibold text-[#8B2252] whitespace-nowrap">from ${(from * (1 - pct / 100)).toFixed(2)}</span>
        </p>
      </div>
    </Link>
  );
};

export const SeasonalPromoHero = () => {
  const { activeSeasonal: promo } = useCart();
  const { products } = useCatalog();
  const saleNames = useSaleProductNames();
  if (!promo) return null;
  const items = promo.product_ids.map((s) => products.find((p) => p.slug === s)).filter(Boolean).slice(0, 2);
  const left = promo.end_date ? daysLeft(promo.end_date) : null;
  const accent = promo.accent_color || "#8B2252";

  return (
    <section data-testid="home-seasonal-promo" className="container-x py-10 md:py-14">
      <div className="relative overflow-hidden rounded-[32px] bg-[#F5EAE6] px-6 sm:px-10 md:px-14 py-10 md:py-14">
        <CornerLeaves />
        <AutumnLeaves />
        <div className="relative grid md:grid-cols-[1.1fr_1fr] gap-10 md:gap-14 items-center">
          <div>
            <span className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.22em] text-[#8B2252]">
              {promo.icon === "ghost" ? null : <Leaf className="w-3.5 h-3.5" strokeWidth={2} />}
              {promo.name}
            </span>
            <h2 className="mt-4 font-display font-bold text-4xl sm:text-5xl leading-[1.02] tracking-tight text-[#1C1618]">
              <span data-testid="home-seasonal-pct" style={{ color: accent }}>{promo.percent}% off</span>{" "}
              <span className="text-[#8B2252]">{saleNames}</span>
              {promo.icon === "ghost" && (
                <GhostMark className="inline-block align-middle ml-3 -mt-2 w-10 h-10 sm:w-11 sm:h-11" testId="home-seasonal-ghost" />
              )}
            </h2>
            <p className="mt-5 text-base md:text-lg leading-relaxed text-[#9a7a80] max-w-md">
              {promo.description || "Every size, applied automatically at checkout."}
              {promo.allow_first10 && " New here? Stack FIRST10 for an extra 10% off your first order."}
            </p>
            <div className="mt-7 flex flex-wrap items-center gap-3">
              {items.map((p) => (
                <Link key={p.slug} to={`/product/${p.slug}`} data-testid={`home-seasonal-cta-${p.slug}`}
                  style={{ background: accent }}
                  className="inline-flex items-center gap-1.5 rounded-full text-white px-6 py-3 text-sm font-medium transition-[filter,transform] duration-300 hover:brightness-90 hover:-translate-y-0.5">
                  Shop {p.name} <ArrowUpRight className="w-4 h-4" strokeWidth={2} />
                </Link>
              ))}
            </div>
            <p data-testid="home-seasonal-terms" className="mt-6 text-xs leading-relaxed text-[#9a7a80]">
              {promo.start_date && promo.end_date && `${fmtDate(promo.start_date)} – ${fmtDate(promo.end_date)}`}
              {left !== null && ` · ${left === 0 ? "Ends today" : `Ends in ${left} day${left === 1 ? "" : "s"}`}`}
              {" · "}{promo.allow_first10 ? "Only FIRST10 can be combined with this sale." : "Cannot be combined with any other discount."} For research use only.
            </p>
          </div>
          <div className="flex gap-4 md:gap-5">
            {items.map((p) => <PromoProduct key={p.slug} product={p} pct={promo.percent} accent={accent} />)}
          </div>
        </div>
      </div>
    </section>
  );
};

export default SeasonalPromoHero;
